import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { AuthNewService } from "src/auth/services/auth-new.service";
import { environment } from "src/environments/environment";

interface TidioChatApi {
  on(event: "ready", callback: () => void): void;
  show(): void;
  hide(): void;
  close?(): void;
}

interface TidioWindow extends Window {
  tidioChatApi?: TidioChatApi;
}

@Injectable({ providedIn: "root" })
export class TidioChatService implements OnDestroy {
  private static readonly SCRIPT_ID = "tidio-chat-script";

  private readonly destroy$ = new Subject<void>();
  private readonly onTidioReady = () => this.applyVisibility();
  private currentUrl: string;
  private initialized = false;
  private isAuthenticated = false;
  private scriptRequested = false;
  private shouldShow = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly router: Router,
    private readonly auth: AuthNewService,
  ) {
    this.currentUrl = this.router.url;
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.document.addEventListener("tidioChat-ready", this.onTidioReady);

    this.auth.auth$.pipe(takeUntil(this.destroy$)).subscribe((auth) => {
      this.isAuthenticated = Boolean(auth);
      this.syncWidget();
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        this.currentUrl = event.urlAfterRedirects;
        this.syncWidget();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.document.removeEventListener("tidioChat-ready", this.onTidioReady);
  }

  private syncWidget(): void {
    this.shouldShow =
      this.isAuthenticated && this.isApaiRoute(this.currentUrl);

    if (this.shouldShow) {
      this.loadScript();
    }

    this.applyVisibility();
  }

  private loadScript(): void {
    const publicKey = environment.tidioPublicKey?.trim();
    if (!publicKey || this.scriptRequested) {
      return;
    }

    const scriptUrl = `https://code.tidio.co/${publicKey}.js`;
    const existingScript =
      this.document.getElementById(TidioChatService.SCRIPT_ID) ||
      this.document.querySelector(`script[src="${scriptUrl}"]`);

    if (existingScript) {
      this.scriptRequested = true;
      return;
    }

    this.scriptRequested = true;
    const script = this.document.createElement("script");
    script.id = TidioChatService.SCRIPT_ID;
    script.src = scriptUrl;
    script.async = true;
    script.addEventListener("load", () => this.applyVisibility());
    script.addEventListener("error", () => {
      this.scriptRequested = false;
      script.remove();
    });

    (this.document.body || this.document.head).appendChild(script);
  }

  private applyVisibility(): void {
    const api = (this.document.defaultView as TidioWindow | null)?.tidioChatApi;
    if (!api) {
      return;
    }

    if (this.shouldShow) {
      api.show();
      return;
    }

    api.close?.();
    api.hide();
  }

  private isApaiRoute(url: string): boolean {
    const path = url.split(/[?#]/, 1)[0];
    return path === "/app/apai" || path.startsWith("/app/apai/");
  }
}
